import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import {
  useConsumerEmail,
  useConsumerName,
  useAuthResetCodeMutation,
  useConsumerMobile,
  useAuthCheckCodeMutation,
  useEmailEnabled,
  authErrorArray,
  useSMSEnabled,
} from '../../state'
import { Button, CodeEntry, Heading, Text, useToast } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../utils/responsive'
import { useCodeEntry } from '../../hooks'
import { useRouter } from 'next/router'
import { Route } from '../../config/router'
import { ProfileCard } from '../../component/ProfileCard'
import { VerificationMethod } from '@afterpaytouch/registration'
import { maskPhoneNumber } from '@afterpay/utils'
import { AuthCheckCodeStatus } from '@afterpaytouch/portal-api/consumer/auth/types'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { ErrorCode, isPortalApiError } from '@afterpaytouch/portal-api'

const I18N_NAMESPACE = ['common', 'password']

interface SendCodeLinkProps {
  children: ReactNode
}

export const CreatePasswordVerify: FunctionComponent = () => {
  const { codeEntryRef, codeEntryKey, resetCodeEntry } = useCodeEntry()
  const { headingSize, textSize } = useConsumerSizes()
  const mobile = maskPhoneNumber(useConsumerMobile())
  const router = useRouter()
  const toast = useToast()
  const { t } = useTranslation(I18N_NAMESPACE)
  const consumerName = useConsumerName()
  const email = useConsumerEmail() ?? ''
  const [authResetCode] = useAuthResetCodeMutation()
  const [codeEntryErrorMessage, setCodeEntryErrorMessage] = useState('')
  const [authCheckCode] = useAuthCheckCodeMutation()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const emailEnabled = useEmailEnabled()
  const smsEnabled = useSMSEnabled()
  const [method, setMethod] = useState<VerificationMethod>(
    smsEnabled ? VerificationMethod.SMS : emailEnabled ? VerificationMethod.EMAIL : VerificationMethod.SMS
  )
  const sendHandler = async (m: VerificationMethod): Promise<void> => {
    try {
      setCodeEntryErrorMessage('')
      await authResetCode({
        sendBy: m,
        email: email ?? '',
      }).unwrap()
      toast({
        message: t('password:verify:codeSent'),
        kind: 'Success',
      })
      logEvent(TrackingEvent.SEND_CODE_ON_CREATE_PASSWORD_VERIFY, {
        method: m,
        success: true,
      })
    } catch (e) {
      const errorCode = isPortalApiError(e.data) ? e.data.errorCode : ErrorCode.Error
      toast({
        message: t(`password:verify:error:codeSentFail_${m}`, { email, mobile }),
        kind: 'Failure',
      })
      logEvent(TrackingEvent.SEND_CODE_ON_CREATE_PASSWORD_VERIFY, {
        method: m,
        success: false,
        error: e?.data?.errorCode,
      })
      if (authErrorArray.includes(errorCode)) {
        router.push(Route.INDEX)
      }
    }
  }

  const onCodeEntryComplete = async (code: string): Promise<void> => {
    try {
      setCodeEntryErrorMessage('')
      const checkCodeResult = await authCheckCode({ code, email }).unwrap()
      if (checkCodeResult.status === AuthCheckCodeStatus.TWO_FACTOR_REQUIRED) {
        router.push(Route.VERIFY_CHALLENGE)
      } else {
        router.push(Route.CREATE_PASSWORD)
      }
    } catch (e) {
      resetCodeEntry()
      setCodeEntryErrorMessage(t('password:verify:error:default'))
      toast({
        message: t('password:verify:error:default'),
        kind: 'Failure',
      })
    }
  }
  const reSendHandler = (m: VerificationMethod): void => {
    logEvent(TrackingEvent.PRESSED_RESEND_CODE, {
      method: m,
    })
    setMethod(m)
    sendHandler(m)
  }

  const renderResendLink = (): ReactNode => {
    if (smsEnabled && emailEnabled) {
      // Both methods are available
      return <Trans i18nKey={`password:verify:${method.toLocaleLowerCase()}:sendLink:both`} components={components} />
    } else if (smsEnabled) {
      // Only SMS is available
      return <Trans i18nKey={`password:verify:${method.toLocaleLowerCase()}:sendLink:sms`} components={components} />
    } else if (emailEnabled) {
      // Only EMAIL is available
      return <Trans i18nKey={`password:verify:${method.toLocaleLowerCase()}:sendLink:email`} components={components} />
    } else {
      // Default fallback - show SMS option (API will handle errors)
      return <Trans i18nKey={`password:verify:${method.toLocaleLowerCase()}:sendLink:sms`} components={components} />
    }
  }

  const SendCodeLink = (m: VerificationMethod): FunctionComponent => {
    const component: FunctionComponent<SendCodeLinkProps> = ({ children }) => (
      <Button.Link size='Regular' onClick={() => reSendHandler(m)} testNameSpace={`${m}-resend`}>
        {children}
      </Button.Link>
    )
    component.displayName = `Send${m}Link`
    return component
  }
  const SendSMS = SendCodeLink(VerificationMethod.SMS)
  const SendEmail = SendCodeLink(VerificationMethod.EMAIL)
  const components = { sms: <SendSMS />, email: <SendEmail /> }

  useEffect(() => {
    sendHandler(method)
  }, [])

  return (
    <div>
      <Heading size={headingSize} data-testid='header'>
        {t('password:verify:heading')}
      </Heading>
      <div className='mb-4 mt-4'>
        <ProfileCard consumerName={consumerName} consumerEmail={email ?? ''} />
      </div>
      <Text>{t(`password:verify:${method.toLocaleLowerCase()}:description`, { mobile, email })}</Text>
      <div className='mt-4'>
        <CodeEntry
          name='verify-code'
          aria-labelledby='verify-code'
          testNameSpace='verify'
          codeEntryLength={6}
          onComplete={onCodeEntryComplete}
          error={codeEntryErrorMessage !== ''}
          errorText={codeEntryErrorMessage}
          ref={codeEntryRef}
          key={codeEntryKey}
        />
      </div>
      <div className='mt-6 text-center'>
        <Text size={textSize}>{renderResendLink()}</Text>
      </div>
    </div>
  )
}
